import type { IH2OTheme } from '../../models/components.models';
import { FontSizes, FontWeights } from '../../themes/styles';
import { mediaMobile, mediaNoMobile } from '../../utils/styles';
import {
  narrowSectionWidth,
  narrowSectionWidthMobile,
  wideSectionWidth,
  wideSectionWidthMobile,
} from './components/NavigationSection/constants';
import type { AppBarNavigationStyles } from './types';

const flexCenter = { display: 'flex', flexDirection: 'column', justifyContent: 'center' };

export const showElements = {
  opacity: 1,
  transform: 'translateX(0)',
};
export const hideElements = {
  opacity: 0,
  transform: 'translateX(-100%)',
};

const appBarHeight = 60;

export const appBarNavigationStylesDefault = (theme: IH2OTheme): AppBarNavigationStyles => ({
  root: {
    position: 'relative',
    display: 'flex',
    width: '100%',
    height: appBarHeight,
    padding: '0 8px',
    zIndex: 10,
    ':before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: theme.palette?.white,
      boxShadow: '0 5px 20px 0 rgba(0, 0, 0, 0.05)',
      zIndex: 4,
    },

    '&.main-nav-compact': {
      '.h2o-NavigationElements-element:not(.element-has-image) > .element-wrapper': {
        background: 'transparent !important',
      },
      '.h2o-NavigationSection-rootWrapper': {
        '::-webkit-scrollbar': { display: 'none' },
      },
    },

    // Expanded navigation with a single active elements level
    '&.main-nav-expand-hard:not(.main-nav-two-plus-levels)': {
      // Wide Root Section
      '> .h2o-NavigationSection-root': {
        ...mediaNoMobile({ width: wideSectionWidth }),
        ...mediaMobile({ width: wideSectionWidthMobile }),
      },
      // Show Visual elements
      '.h2o-NavigationElements-elementTitle, .h2o-NavigationElementsMore-root, .h2o-NavigationBlocks-title': {
        ...showElements,
      },
      // Show Immediate Section inside an active Navigation Element
      '.h2o-NavigationElements-element.element-active > .h2o-NavigationSection-root': { ...showElements },
      // Hide Immediate Section inside an active Navigation Element within a higher active Navigation Element
      '.h2o-NavigationElements-element.element-active .element-active > .h2o-NavigationSection-root': {
        ...hideElements,
      },
    },
    // Expanded navigation with multiple active elements level
    '&.main-nav-expand-hard.main-nav-two-plus-levels': {
      // Narrow Root Section
      '> .h2o-NavigationSection-root': {
        ...mediaNoMobile({ width: narrowSectionWidth }),
        ...mediaMobile({ width: narrowSectionWidthMobile }),

        // Disable collapsed elements for clicking
        '.h2o-NavigationBlocks-block': { ...mediaMobile({ pointerEvents: 'none' }) },
        // Enable expanded elements for clicking
        '.h2o-NavigationBlocks-block .h2o-NavigationBlocks-block': { ...mediaMobile({ pointerEvents: 'all' }) },
        // Move navigation icons
        '.h2o-NavigationElements-elementImage': { ...mediaMobile({ transform: 'translateX(5px)' }) },
      },
      // Hide Visual elements
      '.h2o-NavigationElements-elementTitle, .h2o-NavigationElementsMore-root, .h2o-NavigationBlocks-title': {
        ...hideElements,
      },
      // Show Immediate Section inside an active Navigation Element
      '.h2o-NavigationElements-element.element-active > .h2o-NavigationSection-root': {
        ...showElements,
        '.h2o-NavigationElements-elementTitle, .h2o-NavigationElementsMore-root, .h2o-NavigationBlocks-title': {
          ...showElements,
        },
      },
      // Hide Immediate Section inside an active Navigation Element within a higher active Navigation Element
      '.h2o-NavigationElements-element.element-active .element-active > .h2o-NavigationSection-root': {
        ...hideElements,
      },
    },
    // Expanded sub element
    '&.main-nav-expand-hard:not(.main-nav-expand-soft)': {
      '.h2o-NavigationSection-rootWrapper': {
        '::-webkit-scrollbar': { display: 'none' },
      },
    },
    // Mouseover navigation with a single and multiple active elements level
    '&.main-nav-expand-soft, &.main-nav-expand-soft.main-nav-two-plus-levels': {
      // Wide Root Section
      '> .h2o-NavigationSection-root': {
        ...mediaNoMobile({ width: wideSectionWidth }),
        ...mediaMobile({ width: wideSectionWidthMobile }),

        // Disable collapsed elements for clicking
        '.h2o-NavigationBlocks-block': { ...mediaMobile({ pointerEvents: 'all' }) },
        // Enable expanded elements for clicking
        '.h2o-NavigationBlocks-block .h2o-NavigationBlocks-block': { ...mediaMobile({ pointerEvents: 'none' }) },
        // Move navigation icons
        '.h2o-NavigationElements-elementImage': { ...mediaMobile({ transform: 'translateX(0)' }) },
      },
      // Show visual elements
      '.h2o-NavigationElements-elementTitle, .h2o-NavigationElementsMore-root, .h2o-NavigationBlocks-title': {
        ...showElements,
      },
      // Partially Hide Immediate Section inside an active Navigation Element
      '.h2o-NavigationElements-element.element-active > .h2o-NavigationSection-root': {
        opacity: 0.5,
        transform: 'translateX(-70%)',
      },
      // Hide Immediate Section inside an active Navigation Element within a higher active Navigation Element
      '.h2o-NavigationElements-element.element-active .element-active > .h2o-NavigationSection-root': {
        ...hideElements,
      },
    },

    // Visual elements
    '.h2o-NavigationElements-elementTitle, .h2o-NavigationElementsMore-root, .h2o-NavigationBlocks-title': {
      opacity: 0,
      transform: 'translateX(-20%)',
    },

    // Root Section
    '> .h2o-NavigationSection-root': {
      position: 'absolute',
      top: '100%',
      height: `calc(100vh - ${appBarHeight}px)`,
      left: 0,
      zIndex: 3,
      padding: '4px 0',
      transition: 'width 0.3s ease',
      ...mediaNoMobile({ width: narrowSectionWidth }),
      ...mediaMobile({ width: 0 }),
    },
    // Nested Sections excluding the root
    '.h2o-NavigationSection-root .h2o-NavigationSection-root': {
      position: 'absolute',
      left: '100%',
      top: 0,
      zIndex: -2,
      borderLeft: `1px solid ${theme.palette?.gray300}`,
      transition: 'transform 0.3s ease, opacity 0.3s ease',
      padding: '16px 0',
      ...mediaNoMobile({ width: wideSectionWidth }),
      ...mediaMobile({ width: wideSectionWidthMobile }),
      ...hideElements,
      '&:before': { background: '#fdfdfd' },
    },

    // Immediate Section inside an active Navigation Element
    '.h2o-NavigationElements-element.element-active > .h2o-NavigationSection-root': { ...hideElements },
    // Immediate Section inside an active Navigation Element within a higher active Navigation Element
    '.h2o-NavigationElements-element.element-active .element-active > .h2o-NavigationSection-root': { ...hideElements },
  },
  column: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    zIndex: 4,
    ':last-child': {
      display: 'flex',
      justifyContent: 'flex-end',
    },
  },
  logo: {
    cursor: 'pointer',
    width: 40,
    height: 40,
    '.ms-Image, img': {
      maxWidth: '100%',
      maxHeight: '100%',
      width: 'auto',
      height: 'auto',
    },
  },
  menuIcon: {
    width: 40,
    height: 40,
    padding: 8,
    '.ms-Icon': { fontSize: FontSizes.medium },
    ...flexCenter,
  },
  menuBackCover: {
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    zIndex: -1,
  },
  title: {
    cursor: 'pointer',
    color: theme.palette?.primary800,
    fontWeight: FontWeights.semiBold,
    fontSize: FontSizes.medium,
    marginLeft: 14,
    ...flexCenter,
  },
  userMenu: {
    paddingRight: 8,
    ...mediaNoMobile({ paddingLeft: 32 }),
    ...flexCenter,
  },
  headerContent: {
    padding: '0 16px',
    display: 'flex',
    alignItems: 'center',
  },
});
